// Full list of entries for a single logbook.

import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useLocation, useParams, useSearchParams } from 'react-router-dom';
import { useRootCrumbNavigate } from '../item/RootCrumb';
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';
import Form from 'react-bootstrap/Form';
import {
    DataCard,
    useStatusMessage,
    EditLogbookEntry,
    LogbookHelp,
    PickLogbookAddType,
    Confirm } from '../../components';
import * as Database from '../../utilities/Database.mjs';
import {
    urlForLogbookPickTask,
    urlForLogbookEntry,
    urlForLogbookForId,
    urlForLogbookTrip,
    urlForLogbookYear,
    urlForItem } from '../../fleet-shared/Urls.mjs';
import {
  entriesForTripKey,
  entriesForYear,
  entryTripKey,
  tripFieldForType,
  tripsInLogbook,
} from '../../fleet-shared/LogbookTrips.mjs';
import {
    listColumnsForType,
    supportsTaskCompletion,
    logbookTypeById,
    summarizeLogbook,
    LOGBOOK_TYPE_MAINTENANCE,
} from '../../fleet-shared/LogbookTypes.mjs';
import { serializeLogbook, suggestedFileName } from '../../fleet-shared/LogbookIO.mjs';
import { downloadJson } from '../../utilities/downloadFile.mjs';
import { saveQueue } from '../../utilities/SaveQueue.mjs';
import { syncItemCounterFromEntry, withUpdatedCounter } from '../../utilities/itemCounterSync.mjs';
import { loadPlaceIndex } from '../../utilities/places.mjs';
import { usePlaceIndex } from '../../utilities/usePlaceIndex';
import { entryWithResolvedEndpoints } from '../../fleet-shared/TripRoute.mjs';
import { useMyInfiniteScroll } from '../../components/MyInfiniteScroll';
import { CardHeader } from '../../components/CardHeader';
import { LogbookSummaryCard } from './LogbookSummaryCard';
import { ItemBreadcrumbs } from '../item/ItemBreadcrumbs';
import LogbookEntriesTable from './LogbookEntriesTable';
import LogbookTripsTable from './LogbookTripsTable';
import LogbookViewControls from './LogbookViewControls';
import PhotoErrorAlert from './PhotoErrorAlert';
import { useEntryPhotos } from './useEntryPhotos';
import { useLogbookViewPrefs } from './useLogbookViewPrefs';

import { generateClient } from 'aws-amplify/api';
const client = generateClient();

// What a trips-view row with no trip name is called; the wording belongs to
// the page (LogbookYear has its own copy for the same reason).
const UNFILED = '(no trip name)';

const completedTasksByLogbookIdSelection = /* GraphQL */ `
  completedTasksByLogbookId(
    logbookId: $logbookId
    date: $date
    sortDirection: $sortDirection
    filter: $filter
    limit: $limit
    nextToken: $nextToken
  ) {
    items {
      id
      name
      itemId
      logbookId
      date
      itemCounterId
      taskId
      description
      taskNotes
      dueCounter
      dueDate
      counterInterval
      daysInterval
      monthsInterval
      notes
      counter
      cost
      fixByEmail
      fields
      owner
      createdAt
      updatedAt
      __typename
    }
    nextToken
    __typename
  }
`;

const completedTasksByLogbookIdQuery = /* GraphQL */ `
  query CompletedTasksByLogbookId(
    $logbookId: ID!
    $date: ModelStringKeyConditionInput
    $sortDirection: ModelSortDirection
    $filter: ModelTaskPogoCompletedTaskFilterInput
    $limit: Int
    $nextToken: String
  ) {
    ${completedTasksByLogbookIdSelection}
  }
`;


const Logbook = () => {
  const { itemId, logbookId } = useParams();
  const location = useLocation();
  const navigate = useRootCrumbNavigate();

  const [showConfirm, setShowConfirm] = useState(false);
  const [entryToDelete, setEntryToDelete] = useState(null);
  const [item, setItem] = useState(null);
  const [logbook, setLogbook] = useState(null);

  // Rename / delete-logbook UI. The ref mirrors renameValue so the queued
  // autosave reads the newest text when it actually runs.
  const [showRename, setShowRename] = useState(false);
  const [renameValue, setRenameValue] = useState('');
  const renameValueRef = useRef('');
  const [showDeleteLogbook, setShowDeleteLogbook] = useState(false);

  const [StatusMessage, {showText, showError, hideStatus}] = useStatusMessage();

  // Load the item (for the edit form's counter select + breadcrumbs) and the
  // logbook (for its name / type). These are cheap and independent of the
  // paginated entry list below.
  useEffect(() => {
    let mounted = true;
    async function load() {
      try {
        const [loadedItem, loadedLogbook] = await Promise.all([
          Database.retrieveItem(itemId),
          Database.retrieveLogbook(logbookId),
        ]);
        if (!mounted) return;
        setItem(loadedItem);
        setLogbook(loadedLogbook);
      } catch (err) {
        showError(err);
      }
    }
    load();
    return () => { mounted = false; };
  }, [itemId, logbookId, showError]);

  // A warning the importing page couldn't show, because it navigated here.
  // Shown once, beside the logbook it is about, rather than on the page the
  // user has already left. Only a places-phase failure produces one -- the
  // import itself succeeded, or we wouldn't be here.
  useEffect(() => {
    if (location.state?.notice) showText(location.state.notice);
    // Mount only: re-firing on a later render would resurrect a dismissed
    // notice, and router state survives a reload.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const typeId = logbook?.logbookType || LOGBOOK_TYPE_MAINTENANCE;
  const columns = listColumnsForType(typeId);

  // Which column links to a per-trip page: the type's own trip field
  // ('tripName' on a Captain's Log), or null for the Pilot's Logbook and
  // Maintenance, which have no notion of a trip. That single expression is the
  // whole "which logbooks get trip pages" rule -- no type ids hard-coded here.
  const tripKey = tripFieldForType(typeId)?.key ?? null;

  // Which view: entries is this page's default, so only `?view=trips` is ever
  // in the URL (TripsCalendarSection's contract -- absent means the default
  // and is never written back; every write replaces, so Back leaves the page
  // rather than walking through views). A type with no trips ignores the
  // param outright.
  const [searchParams, setSearchParams] = useSearchParams();
  const view = tripKey && searchParams.get('view') === 'trips' ? 'trips' : 'entries';
  const setView = useCallback(
    (next) => {
      setSearchParams(
        (params) => {
          const updated = new URLSearchParams(params);
          if (next === 'trips') updated.set('view', 'trips');
          else updated.delete('view');
          return updated;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  // The persisted Show notes / Show photos switches, shared with the trip and
  // year pages.
  const [viewPrefs, setViewPref] = useLogbookViewPrefs();

  // ONE item-wide S3 listing for the whole page, which is why a list that
  // grows by paging costs no more than the trip page's fixed one: the map is
  // keyed by entry id and has no `entryIds` dep, so rows scrolled in later
  // find their photos already in it. The switch is the hook's `enabled` AND
  // what the tables are given below -- one boolean, so "we fetched" and "we
  // render" cannot disagree, and off is a mount gate on the read.
  //
  // Offered on EVERY logbook type, Maintenance included: an entry carries
  // attachments whatever its type, and the listing is one round trip either
  // way. Deliberately unlike `onSelectYear`, which is gated on `tripKey`
  // because a per-year PAGE only exists for trip types.
  const { photosByEntry, photosError, openPhoto } = useEntryPhotos(itemId, {
    enabled: viewPrefs.showPhotos,
  });

  // The gazetteer, for the From/To columns. Most logbooks are Maintenance and
  // have no place column at all, and loadPlaceIndex is a whole-table read --
  // so only ask when there is something to shorten. `typeId` starts as
  // Maintenance until `logbook` lands, so a Captain's Log paints its full names
  // once and shortens when the index arrives, the same two-pass the map's
  // route overlay already has.
  const hasPlaceColumn = columns.some((field) => field.type === 'place');
  const placeIndex = usePlaceIndex({ enabled: hasPlaceColumn });

  // For logbook types that show a summary card (Captain's / Pilot's), load the
  // full set of entries once (independent of the paginated list below) so the
  // summary can aggregate the whole logbook. Kept in sync with edits via the
  // mutation handlers.
  const [allEntries, setAllEntries] = useState(null);
  // Without this the skeleton below would shimmer for the life of the page
  // after a failed fetch: `allEntries` stays null forever and the error only
  // ever reaches the status message.
  const [allEntriesFailed, setAllEntriesFailed] = useState(false);
  useEffect(() => {
    if (!logbook || !logbookTypeById(typeId).summary) return;
    let mounted = true;
    Database.listCompletedTasksForLogbook(logbookId)
      .then((all) => { if (mounted) setAllEntries(all); })
      .catch((err) => {
        if (mounted) setAllEntriesFailed(true);
        showError(err);
      });
    return () => { mounted = false; };
  }, [logbook, typeId, logbookId, showError]);

  const summary = useMemo(
    () => (allEntries ? summarizeLogbook(allEntries, typeId) : null),
    [allEntries, typeId]
  );

  // The trips view's rows, off the same whole-logbook read the summary card
  // already does -- which is what makes trip grouping here cheap. Null until
  // that read lands; every mutation handler keeps `allEntries` current, so
  // the rows track edits too.
  const tripRows = useMemo(
    () => (allEntries && tripKey ? tripsInLogbook(allEntries, typeId) : null),
    [allEntries, tripKey, typeId]
  );

  // Is the summary still coming? `allEntries === null` alone can't answer
  // that: it is ALSO null forever on a Maintenance log, where the effect above
  // early-returns. And `logbook` has to have landed, because typeId defaults to
  // Maintenance until it does -- showing a Summary card on a Maintenance log
  // that then vanishes is a worse reflow than the small one when the logbook
  // resolves.
  const summaryLoading =
    Boolean(logbook) &&
    Boolean(logbookTypeById(typeId).summary) &&
    allEntries === null &&
    !allEntriesFailed;

  // The shared entries table, with the two capabilities only this page asks
  // for: a Trip Name that links to its trip page, and a trailing Details arrow.
  // This page used to carry a private row of its own; the continuation row's
  // colSpan was hardcoded there and would have been one short the moment
  // photos could appear with notes off, which is the default.
  //
  // The rows it is given are DISPLAY copies (entryWithResolvedEndpoints) --
  // the table's contract. A From/To column is a mirror of the route written at
  // save time, so a place renamed in /me/places since then is reachable only
  // through the waypoint's placeId. Both handlers below re-look-up the stored
  // row before anything leaves this page.
  //
  // NOT memoized, and it must not be: this closure is what carries
  // photosByEntry, openPhoto, viewPrefs and placeIndex into a component that
  // reads it through a ref (MyInfiniteScroll's `latest`). A useCallback missing
  // a dep would freeze the list on stale values with no error anywhere, and
  // Control's stable identity means React would never remount out of it.
  const renderChildren = (rows) => (
    <LogbookEntriesTable
      entries={(rows ?? []).map((entry) => entryWithResolvedEndpoints(entry, placeIndex))}
      columns={columns}
      placeIndex={placeIndex}
      showNotes={viewPrefs.showNotes}
      onSelectEntry={onEntryRow}
      tripKey={tripKey}
      onTripName={onEntryTripName}
      showDetails
      photosByEntry={viewPrefs.showPhotos ? photosByEntry : null}
      onOpenPhoto={openPhoto}
    />
  );

  // Go get the next page of entries.
  const fetchMoreEntries = useCallback(async (pageToken) => {
    const query = {
      query: completedTasksByLogbookIdQuery,
      variables: {
        logbookId: logbookId,
        sortDirection: 'DESC',
        ...(pageToken && { nextToken: pageToken })
      }
    };
    let results = await client.graphql(query);

    return {
      nextToken: results.data.completedTasksByLogbookId.nextToken,
      items: results.data.completedTasksByLogbookId.items
    };
  }, [logbookId]);

  const endMessage = () => (
    <div className="text-center text-body-secondary py-3 small">
      — End of logbook —
    </div>
  );

  const [entries, setEntries] = useState(null);


  const { Control } = useMyInfiniteScroll(
    renderChildren,
    fetchMoreEntries,
    entries,
    setEntries,
    endMessage
  );

  const [showEditLogbookEntry, setShowEditLogbookEntry] = useState(false);
  const [editLogbookEntry, setEditLogbookEntry] = useState(null);
  const [showPickLogbook, setShowPickLogbook] = useState(false);


  // Called when a new entry is added to the DB. Shows in UI and starts editing it.
  function onAdded(newTask) {
    let newEntries = [newTask, ...entries];
    setEntries(newEntries);
    setAllEntries((prev) => (prev ? [newTask, ...prev] : prev));
    setEditLogbookEntry(newTask);
    setShowEditLogbookEntry(true);
    setShowPickLogbook(false);
  }

  async function handleAdd() {
    await Database.createCompletedTaskWithUI(logbook, showText, showError, hideStatus, onAdded);
  }

  // The freshest item/type for the counter sync below: saveEditedTask is
  // memoized on setEntries alone, so reading the state directly there would
  // see the mount-time copies (null item, Maintenance type) forever.
  const syncContextRef = useRef({ item: null, typeId });
  syncContextRef.current = { item, typeId };

  // Passed to EditLogbookEntry. `task` is already merged (id/itemId/logbookId +
  // split column/json fields). throws on errors.
  const saveEditedTask = useCallback(async (task) => {
    // Update the database, can throw.
    const saved = await Database.updateCompletedTask(task);

    // And UI: replace the matching entry. Runs per autosave flush -- must
    // NOT close the modal.
    setEntries((prev) => (prev ?? []).map((e) => (e.id === saved.id ? saved : e)));
    setAllEntries((prev) => (prev ? prev.map((e) => (e.id === saved.id ? saved : e)) : prev));

    // The last entry of a Captain's Log moves the item's own counter (see
    // itemCounterSync.mjs for the guards). Fire-and-forget: a failed SYNC
    // must not fail the entry save that triggered it.
    const { item: currentItem, typeId: currentTypeId } = syncContextRef.current;
    syncItemCounterFromEntry({
      entry: saved,
      typeId: currentTypeId,
      item: currentItem,
      onCounterSaved: (counter) => setItem((prev) => withUpdatedCounter(prev, counter)),
    }).catch((err) => console.error('Item counter sync failed', err));
  }, [setEntries]);


  // User clicked delete, confirm.
  function onClickDelete(entry) {
    setEntryToDelete(entry);
    setShowConfirm(true);
  }

  // User confirmed delete, go delete.
  async function onConfirmDelete() {
    showText('Deleting');

    await Database.deleteCompletedTask(entryToDelete.id);

    // Update model
    let tasks = entries.filter((task) => task.id !== entryToDelete.id);
    hideStatus();

    setEntries(tasks);
    setAllEntries((prev) => (prev ? prev.filter((task) => task.id !== entryToDelete.id) : prev));
    setShowConfirm(false);
    setShowEditLogbookEntry(false);
  }


  function handleSelectTaskForLogbook() {
    navigate(urlForLogbookPickTask(itemId, logbookId), { state: { item, logbook } });
  }

  // Rename the logbook. The name autosaves as the user types (debounced,
  // through the shared save queue); an emptied name keeps the old one, same
  // rule the Save button used to apply. Done/close flushes any pending edit.
  function openRename() {
    setRenameValue(logbook?.name ?? '');
    renameValueRef.current = logbook?.name ?? '';
    setShowRename(true);
  }

  function renameChanged(value) {
    setRenameValue(value);
    renameValueRef.current = value;
    saveQueue.schedule(
      `logbook:${logbook.id}`,
      {
        run: () =>
          Database.updateLogbook({
            id: logbook.id,
            name: (renameValueRef.current || '').trim() || logbook.name,
          }),
        onSaved: (saved) => setLogbook(saved),
      },
      { delayMs: 1500 }
    );
  }

  function closeRename() {
    saveQueue.flush(`logbook:${logbook.id}`);
    setShowRename(false);
  }

  // Delete the whole logbook (cascade entries), then return to the item page.
  // Entries are deleted one at a time, so report progress through the confirm
  // dialog's own status line -- the page's status message is behind the modal.
  async function confirmDeleteLogbook(status) {
    try {
      status.showLoading('Deleting logbook...');

      // Cancel any pending or in-flight rename first -- a late update
      // against the deleted logbook must neither error nor resurrect it.
      saveQueue.cancel(`logbook:${logbook.id}`);

      await Database.deleteLogbook(logbook.id, {
        onProgress: (done, total) =>
          status.showLoading(`Deleted ${done} of ${total} ${total === 1 ? 'entry' : 'entries'}...`),
      });
      status.hideStatus();
      navigate(urlForItem(itemId), { replace: true });
    } catch (err) {
      setShowDeleteLogbook(false);
      showError(err);
    }
  }


  // Export every entry in this logbook to a downloaded JSON file.
  async function handleExport() {
    try {
      showText('Exporting...');
      // The gazetteer alongside the entries: the file carries the place rows
      // its routes refer to, so importing it into another environment can
      // recreate them instead of stranding the bindings. loadPlaceIndex never
      // rejects and resolves to empty maps on failure, which simply omits the
      // section -- an export must not fail because the place list is down.
      const [allEntries, placeIndex] = await Promise.all([
        Database.listCompletedTasksForLogbook(logbookId),
        loadPlaceIndex(),
      ]);
      const payload = serializeLogbook(logbook, allEntries, { placeIndex });
      downloadJson(suggestedFileName(logbook), payload);
      hideStatus();
    } catch (err) {
      showError(err);
    }
  }

  // Open the per-trip page for the trip THIS entry is filed under. Keyed off
  // the entry rather than the name it shows, because several trips can share a
  // name -- that is the whole reason a trip has an id.
  //
  // `allEntries` is the whole-logbook read the summary card already does, so
  // hand over this trip's rows for an instant first paint when it has landed.
  // When it hasn't, hand over nothing -- the page always fetches anyway, and a
  // partial set would look like the whole trip. `tripName` rides along either
  // way so the heading doesn't arrive a beat after the page.
  function onTripName(entry, tripName) {
    const key = entryTripKey(entry, typeId);
    navigate(urlForLogbookTrip(itemId, logbookId, key), {
      state: {
        item,
        logbook,
        tripName,
        ...(allEntries ? { entries: entriesForTripKey(allEntries, key, typeId) } : {}),
      },
    });
  }

  // Open the per-year page from a summary-card year row. Mirrors onTripName,
  // handover rule included.
  function onSelectYear(year) {
    navigate(urlForLogbookYear(itemId, logbookId, year), {
      state: {
        item,
        logbook,
        ...(allEntries ? { entries: entriesForYear(allEntries, year) } : {}),
      },
    });
  }

  // Click on row, go to details.
  function onDetails(entry) {
    navigate(urlForLogbookEntry(itemId, logbookId, entry.id), {
      state: { item, logbook, fromLogbookList: true },
    });
  }

  // The entries view's rows are display copies of the INFINITE-SCROLL state, so
  // the stored row is looked up in `entries` -- not `allEntries`, which is a
  // different read and is null forever on a type with no summary card.
  function storedEntryFor(displayEntry) {
    return (entries || []).find((entry) => entry.id === displayEntry.id) || null;
  }

  // A row click or its Details arrow -- one destination, which is why the table
  // takes a `showDetails` boolean rather than a second handler.
  function onEntryRow(displayEntry) {
    const stored = storedEntryFor(displayEntry);
    if (stored) onDetails(stored);
  }

  // The Trip Name link. onTripName needs the STORED entry: entryTripKey reads
  // the `fields` blob, and a resolved display copy must never be what decides
  // which trip a row is filed under.
  function onEntryTripName(displayEntry, tripName) {
    const stored = storedEntryFor(displayEntry);
    if (stored) onTripName(stored, tripName);
  }

  // A trips-view row: a named one opens the trip page (with the whole trip's
  // slice -- `allEntries` is loaded by construction in that view, or there
  // would be no rows); an unfiled one has no trip to open, so it opens its one
  // entry. Never entriesForTripKey for an unfiled 'e:' row -- it refuses
  // non-joinable keys and answers [] -- the row already carries its entry.
  function onTripRow(row) {
    if (row.name) {
      navigate(urlForLogbookTrip(itemId, logbookId, row.key), {
        state: {
          item,
          logbook,
          tripName: row.name,
          entries: entriesForTripKey(allEntries ?? [], row.key, typeId),
        },
      });
      return;
    }
    const entry = row.entries[0];
    if (entry) onDetails(entry);
  }

  // An entry inside an expanded trips-view row. The table hands back a display
  // copy; re-look-up the stored row before navigating (useEntryMapLegs' rule).
  function onTripRowEntry(displayEntry) {
    const stored = (allEntries || []).find((entry) => entry.id === displayEntry.id);
    if (stored) onDetails(stored);
  }

  // Show the entries in a table
  return (
    <div className='pb-2'>
      <ItemBreadcrumbs
        item={item}
        extras={logbook ? [{ name: logbook.name, url: urlForLogbookForId(itemId, logbookId) }] : []}
      />

      <StatusMessage />

      {summaryLoading
        ? <LogbookSummaryCard loading />
        : summary && summary.byYear.length > 0 && (
            // `tripKey` IS the "which logbooks have trips" rule (tripFieldForType);
            // without one there is no per-year page to open, so the rows stay
            // plain text rather than leading somewhere that lists flights.
            <LogbookSummaryCard
              summary={summary}
              onSelectYear={tripKey ? onSelectYear : undefined}
            />
          )}

      { (entries && entries.length === 0) ? <LogbookHelp typeId={typeId} /> : null }

      {/* body is unconditional (was gated on `entries`): the hook's error
          StatusMessage renders inside Control, so hiding the body while
          entries was null threw a first-fetch failure away and left the list
          silently empty. The modals inside are show={false} until the user
          acts, so they tolerate entries === null. */}
      <DataCard
        header={
          <CardHeader
            text={logbook ? logbook.name : 'Logbook'}
            extraActions={
              <Button variant="outline-secondary" onClick={handleExport}>
                Export JSON
              </Button>
            }
            addTapped={() => setShowPickLogbook(true)}
            editTapped={openRename}
            deleteTapped={() => setShowDeleteLogbook(true)}
          />
        }
        body={(
          <>
            <LogbookViewControls
              view={view}
              onViewChange={setView}
              showViewToggle={Boolean(tripKey)}
              showNotes={viewPrefs.showNotes}
              onShowNotesChange={(value) => setViewPref('showNotes', value)}
              showPhotosToggle
              showPhotos={viewPrefs.showPhotos}
              onShowPhotosChange={(value) => setViewPref('showPhotos', value)}
            />

            {/* Above the view branch, so it serves the entries list and the
                trips view alike. Never the page's own StatusMessage -- see
                PhotoErrorAlert, which owns the wording and the dismissal. */}
            <PhotoErrorAlert error={photosError} show={viewPrefs.showPhotos} />

            {/* The infinite-scroll hook stays mounted either way; only its
                Control unmounts in the trips view (the initial fetch lives in
                the hook's own effect, so nothing is lost). The trips view
                needs the WHOLE logbook -- a scroll prefix would draw trips too
                short with nothing saying so -- which allEntries is. */}
            {view === 'trips' ? (
              allEntriesFailed ? (
                <p className="text-center text-body-secondary py-3 mb-0">
                  The trips view could not load.{' '}
                  <Button variant="link" className="p-0 align-baseline" onClick={() => setView('entries')}>
                    Show entries
                  </Button>
                </p>
              ) : tripRows === null ? (
                <p className="text-center text-body-secondary py-3 mb-0">Loading trips…</p>
              ) : tripRows.length > 0 ? (
                <LogbookTripsTable
                  rows={tripRows}
                  typeId={typeId}
                  placeIndex={placeIndex}
                  showNotes={viewPrefs.showNotes}
                  onSelectRow={onTripRow}
                  onSelectEntry={onTripRowEntry}
                  unfiledLabel={UNFILED}
                  photosByEntry={viewPrefs.showPhotos ? photosByEntry : null}
                  onOpenPhoto={openPhoto}
                />
              ) : (
                <p className="text-center text-body-secondary py-3 mb-0">
                  No entries in this logbook yet.
                </p>
              )
            ) : (
              <Control />
            )}

            <EditLogbookEntry
              show={showEditLogbookEntry}
              onHide={() => setShowEditLogbookEntry(false)}
              task={editLogbookEntry}
              item={item}
              logbook={logbook}
              save={saveEditedTask}
              deleteEntry={() => onClickDelete(editLogbookEntry)}
            />

            <PickLogbookAddType
              show={showPickLogbook}
              onHide={() => setShowPickLogbook(false)}
              showTaskOption={supportsTaskCompletion(typeId)}
              onSelectTask={() => handleSelectTaskForLogbook()}
              onAddAdHoc={handleAdd}
            />

            <Confirm
              show={showConfirm}
              onConfirm={onConfirmDelete}
              onHide={() => setShowConfirm(false)}
              body='Are you sure?'
              confirmText='Confirm Delete'
              title={`Delete entry`}
            />
          </>
        )}
      />

      <Modal show={showRename} onHide={closeRename}>
        <Modal.Header closeButton>
          <Modal.Title>Rename Logbook</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Control
            type="text"
            value={renameValue}
            onChange={(e) => renameChanged(e.target.value)}
            onBlur={() => saveQueue.flush(`logbook:${logbook.id}`)}
            aria-label="Logbook name"
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeRename}>Done</Button>
        </Modal.Footer>
      </Modal>

      <Confirm
        show={showDeleteLogbook}
        onConfirm={confirmDeleteLogbook}
        onHide={() => setShowDeleteLogbook(false)}
        body='Delete this logbook and all of its entries? This cannot be undone.'
        confirmText='Confirm Delete'
        title={`Delete ${logbook ? logbook.name : 'Logbook'}`}
      />
    </div>
  );
}

export default Logbook;
